import { FeatureFlagController } from '@skand/feature-flags';
import { ReactNode } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

export type ScreenSizeDetectorFeatureFlagConf = {
  'explore-responsive': 'on' | 'off';
};

export class ScreenSizeDetector {
  private _isExploreResponsiveEnabled = false;
  private _isOnMobileScreen = false;
  private _mediaQueryList = matchMedia('(max-width: 640px)');
  private _featureFlagController: FeatureFlagController<ScreenSizeDetectorFeatureFlagConf>;
  private _unsubscribe: null | (() => void) = null;
  private _listeners = new Set<() => void>();

  constructor(args: {
    featureFlagController: FeatureFlagController<ScreenSizeDetectorFeatureFlagConf>;
  }) {
    this._featureFlagController = args.featureFlagController;
    this._update();
  }

  private _update = () => {
    this._isOnMobileScreen = this._mediaQueryList.matches;
    this._isExploreResponsiveEnabled =
      this._featureFlagController.getTreatment('explore-responsive') === 'on';
    this._listeners.forEach(listener => listener());
  };

  start() {
    this._mediaQueryList.addEventListener('change', this._update);
    this._unsubscribe = this._featureFlagController.subscribe(this._update);
  }

  stop() {
    this._mediaQueryList.removeEventListener('change', this._update);
    this._unsubscribe?.();
  }

  subscribe = (callback: () => void) => {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  };

  get isDesktop() {
    return !this.isMobile;
  }

  get isMobile() {
    return this._isOnMobileScreen && this._isExploreResponsiveEnabled;
  }

  useIsMobile() {
    const isMobile = useSyncExternalStore(this.subscribe, () => this.isMobile);
    return isMobile;
  }

  useIsDesktop() {
    return !this.useIsMobile();
  }

  useScreenSize() {
    const isMobile = this.useIsMobile();
    return {
      isMobile,
      isDesktop: !isMobile,
    };
  }

  MobileOnly = ({ children }: { children: ReactNode | (() => ReactNode) }) => {
    const isMobile = this.useIsMobile();
    return isMobile ? (typeof children === 'function' ? children() : children) : null;
  };

  DesktopOnly = ({ children }: { children: ReactNode | (() => ReactNode) }) => {
    const isDesktop = this.useIsDesktop();
    return isDesktop ? (typeof children === 'function' ? children() : children) : null;
  };
}
